/**
 * Password policy, shared by the browser and the server.
 *
 * Here rather than in the API for the same reason the pricing engine lives in
 * `packages/calc`: the client must be able to tell someone their password is too
 * short *before* they submit it, and a second copy of the rule would eventually
 * disagree with the first. The server still enforces it — this is not a client-side
 * check, it is the same check running in two places.
 *
 * Length over composition, following current NIST guidance. Character-class rules
 * ("must contain a symbol") push people towards `Password1!`, which is both harder
 * to remember and easier to guess than four random words.
 */

/** Long enough that online guessing is hopeless, short enough to type on a phone. */
export const MIN_PASSWORD_LENGTH = 12;

/**
 * Argon2 handles long inputs fine, but an unbounded field is a cheap way to make
 * the server do expensive work, so the limit is a denial-of-service guard rather
 * than a password rule.
 */
export const MAX_PASSWORD_LENGTH = 200;

/**
 * The passwords a determined guesser tries first. Deliberately tiny: a real
 * breach-corpus check belongs behind an API, and a 100k-entry list shipped to the
 * browser would cost more than it saves. These are the ones that survive a
 * 12-character minimum, which is what makes them worth naming.
 */
const BLOCKLIST = new Set([
  'passwordpassword',
  'password1234',
  'password12345',
  'passw0rdpassw0rd',
  '123456789012',
  '1234567890123',
  '111111111111',
  'qwertyqwerty',
  'qwertyuiop12',
  'letmeinletmein',
  'iloveyouiloveyou',
  'administrator',
  'christmaslights',
  'holidaylights',
  'zenithzenith',
  'lbhllbhl',
]);

export interface PasswordProblem {
  /** Safe to show the person typing. Never echoes the password back. */
  message: string;
}

/**
 * Returns every reason the password is unacceptable, empty if it is fine.
 *
 * All problems at once rather than the first one, so someone fixing a short
 * password does not then discover it is also blocklisted.
 */
export function checkPasswordPolicy(password: string): PasswordProblem[] {
  const problems: PasswordProblem[] = [];

  if (password.length < MIN_PASSWORD_LENGTH) {
    problems.push({
      message: `Use at least ${MIN_PASSWORD_LENGTH} characters. A few unrelated words is the easiest way to get there.`,
    });
  }

  if (password.length > MAX_PASSWORD_LENGTH) {
    problems.push({ message: `Keep it under ${MAX_PASSWORD_LENGTH} characters.` });
  }

  // Trailing whitespace survives a copy-paste and then fails at sign-in, where
  // the only feedback is "wrong password". Cheaper to refuse it now.
  if (password !== password.trim()) {
    problems.push({ message: 'Remove the space at the start or end.' });
  }

  // Case and separators are noise to a guesser, so normalise before comparing.
  const normalised = password.toLowerCase().replace(/[\s._-]/g, '');
  if (BLOCKLIST.has(normalised)) {
    problems.push({ message: 'That is one of the first passwords anyone would try. Pick something else.' });
  }

  // A single repeated character passes a length check and nothing else.
  if (password.length > 0 && new Set(password).size < 5) {
    problems.push({ message: 'Use a greater variety of characters.' });
  }

  return problems;
}

/** Convenience for the server, which only needs the yes/no and one message. */
export function isPasswordAcceptable(password: string): boolean {
  return checkPasswordPolicy(password).length === 0;
}
